import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { settingsService } from "../services/settingsService";
import SettingModal from "./SettingModal";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import { PlusIcon, PencilIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

const SettingsList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: settingsService.getSettings,
  });

  // Debug: voir la structure des données
  console.log("Settings API Response:", data);

  // S'assurer que settings est toujours un tableau
  const settings = Array.isArray(data)
    ? data
    : data?.data || data?.settings || [];

  const handleEdit = (setting) => {
    setSelectedSetting(setting);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedSetting(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSetting(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["settings"] });
    setIsModalOpen(false);
    setSelectedSetting(null);
  };

  const truncateValue = (value, maxLength = 100) => {
    if (value && value.length > maxLength) {
      return value.substring(0, maxLength) + "...";
    }
    return value;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          Erreur lors du chargement des paramètres
        </div>
        <div className="text-sm text-gray-500 mb-4">
          {error.response?.status === 404
            ? "L'API des paramètres n'est pas encore configurée"
            : error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Paramètres système</h2>
      </div>

      {settings.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">Aucun paramètre configuré</div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 mx-auto px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Ajouter le premier paramètre
          </button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clé
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valeur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {settings.map((setting) => (
                  <tr key={setting.key} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {setting.key}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-md">
                        <pre className="whitespace-pre-wrap font-sans text-xs">
                          {truncateValue(setting.value)}
                        </pre>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(setting)}
                          className="text-indigo-600 hover:text-indigo-900 p-2 rounded hover:bg-indigo-100 transition-colors"
                          title="Modifier"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SettingModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        setting={selectedSetting}
      />
    </div>
  );
};

export default SettingsList;
